// src/pages/TasksDashboardPage.jsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Typography, Card, Stack, Avatar, IconButton, TextField,
  InputAdornment, CircularProgress, Tooltip, alpha, Chip,
  Select, MenuItem, FormControl, InputLabel, Dialog, DialogContent,
  ToggleButtonGroup, ToggleButton, Button, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Search, Close, MyLocation, Refresh, Map, ViewList,
  FileDownload, OpenInNew, CameraAlt, LocationOn, FiberManualRecord,
  Assignment, Person, AccessTime, CheckCircle, Cancel, Info,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { selectToken } from '../store/authSlice';

const API_BASE = 'https://workforce-api.govirtual.ph';

// ── ExcelJS CDN loader ────────────────────────────────────────────────────────
function loadExcelJS() {
  return new Promise((resolve) => {
    if (window.ExcelJS) { resolve(window.ExcelJS); return; }
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/exceljs@4.4.0/dist/exceljs.min.js';
    s.onload = () => resolve(window.ExcelJS);
    document.head.appendChild(s);
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function getLocation(entry) {
  const fd = entry.formData;
  if (!fd) return null;
  // formData may be a plain object or Map-like; normalise
  const loc = fd.location ?? fd.get?.('location');
  if (!loc) return null;
  const lat = parseFloat(loc.latitude);
  const lng = parseFloat(loc.longitude);
  if (isNaN(lat) || isNaN(lng)) return null;
  return { lat, lng };
}

// Flatten formData (handles object or Map) into plain key-value
function flatFormData(fd) {
  if (!fd) return {};
  if (typeof fd.entries === 'function') {
    // Map
    return Object.fromEntries(fd.entries());
  }
  return fd;
}

// Collect all image arrays / single image objects from formData
function extractImages(fd) {
  const flat = flatFormData(fd);
  const imgs = [];
  for (const [key, val] of Object.entries(flat)) {
    if (Array.isArray(val) && val.length && val[0]?.url) {
      val.forEach((img) => imgs.push({ label: key, url: img.url, format: img.format }));
    } else if (val && typeof val === 'object' && val.url && !val.latitude) {
      imgs.push({ label: key, url: val.url, format: val.format });
    }
  }
  return imgs;
}

// Human-friendly key label
function prettyKey(key) {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

// ── Excel export ──────────────────────────────────────────────────────────────
async function exportTasksExcel(entries, dateFrom, dateTo) {
  const ExcelJS = await loadExcelJS();
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Admin Panel';

  // Collect all unique formData keys across all entries
  const allKeys = new Set();
  entries.forEach((e) => {
    const flat = flatFormData(e.formData);
    Object.entries(flat).forEach(([k, v]) => {
      // skip image arrays and location object
      if (Array.isArray(v) && v[0]?.url) return;
      if (v && typeof v === 'object' && v.url) return;
      if (k === 'location') return;
      allKeys.add(k);
    });
  });
  const dataKeys = Array.from(allKeys);

  const ws = wb.addWorksheet('Task Entries', {
    views: [{ state: 'frozen', ySplit: 2 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
  });

  // Fixed columns + dynamic formData columns + image columns
  const fixedCols = [
    { key: 'no',         width: 5  },
    { key: 'taskLabel',  width: 22 },
    { key: 'author',     width: 22 },
    { key: 'synced',     width: 10 },
    { key: 'date',       width: 18 },
    { key: 'lat',        width: 14 },
    { key: 'lng',        width: 14 },
  ];
  const dynCols  = dataKeys.map((k) => ({ key: k, width: 18 }));
  const imgCols  = [
    { key: 'img1', width: 40 },
    { key: 'img2', width: 40 },
    { key: 'img3', width: 40 },
  ];
  ws.columns = [...fixedCols, ...dynCols, ...imgCols];

  // Title row
  const totalCols = fixedCols.length + dynCols.length + imgCols.length;
  ws.mergeCells(1, 1, 1, totalCols);
  const titleCell = ws.getCell('A1');
  titleCell.value = `Task Entries — ${dateFrom} to ${dateTo}`;
  titleCell.font  = { name: 'Arial', size: 13, bold: true, color: { argb: 'FF0f2137' } };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
  titleCell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe6f2ff' } };
  ws.getRow(1).height = 28;

  // Header row
  const headers = [
    '#', 'Task', 'Author', 'Synced', 'Submitted At', 'Latitude', 'Longitude',
    ...dataKeys.map(prettyKey),
    'Photo 1', 'Photo 2', 'Photo 3',
  ];
  const hRow = ws.getRow(2);
  headers.forEach((h, i) => {
    const cell = hRow.getCell(i + 1);
    cell.value = h;
    cell.font  = { name: 'Arial', size: 9, bold: true, color: { argb: 'FFffffff' } };
    cell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1075BA' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = { bottom: { style: 'thin', color: { argb: 'FF0a5a8f' } } };
  });
  hRow.height = 32;

  // Data rows
  entries.forEach((entry, idx) => {
    const flat   = flatFormData(entry.formData);
    const loc    = getLocation(entry);
    const imgs   = extractImages(entry.formData);
    const author = entry.author;

    const rowData = [
      idx + 1,
      entry.taskLabel || entry.taskId || '',
      author ? `${author.firstName || ''} ${author.lastName || ''}`.trim() : '',
      entry.synced ? 'Yes' : 'No',
      entry.createdAt ? format(new Date(entry.createdAt), 'MMM d yyyy hh:mm a') : '',
      loc?.lat ?? '',
      loc?.lng ?? '',
      ...dataKeys.map((k) => {
        const v = flat[k];
        if (v === undefined || v === null) return '';
        if (Array.isArray(v)) return '';        // images handled separately
        if (typeof v === 'object') return '';
        return String(v);
      }),
      ...imgs.slice(0, 3).map((img) => img.url),
      ...Array(Math.max(0, 3 - imgs.length)).fill(''),
    ];

    const dataRow = ws.addRow(rowData);
    const fillColor = idx % 2 === 0 ? 'FFffffff' : 'FFf5f8fc';
    dataRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.font      = { name: 'Arial', size: 9 };
      cell.alignment = { vertical: 'middle', wrapText: true };
      cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
      cell.border    = { bottom: { style: 'thin', color: { argb: 'FFe0e8f0' } } };
    });

    // Synced color
    const syncCell = dataRow.getCell(4);
    syncCell.font = { name: 'Arial', size: 9, bold: true, color: { argb: entry.synced ? 'FF0a9e74' : 'FFc06000' } };

    // Photo hyperlinks
    imgs.slice(0, 3).forEach((img, i) => {
      const col  = fixedCols.length + dynCols.length + i + 1;
      const cell = dataRow.getCell(col);
      if (cell.value) {
        cell.value = { text: `View ${prettyKey(img.label)}`, hyperlink: cell.value };
        cell.font  = { name: 'Arial', size: 9, color: { argb: 'FF1075BA' }, underline: true };
      }
    });

    dataRow.height = 20;
  });

  ws.autoFilter = { from: 'A2', to: `${String.fromCharCode(64 + totalCols)}2` };

  const buffer = await wb.xlsx.writeBuffer();
  const blob   = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url    = URL.createObjectURL(blob);
  const a      = document.createElement('a');
  a.href       = url;
  a.download   = `tasks_${dateFrom}_to_${dateTo}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Photo lightbox ────────────────────────────────────────────────────────────
function PhotoThumb({ src, label }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Tooltip title={prettyKey(label)}>
        <Box
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          sx={{
            width: 40, height: 40, borderRadius: '8px', overflow: 'hidden',
            border: '1.5px solid rgba(16,117,186,0.25)', cursor: 'zoom-in', flexShrink: 0,
            '&:hover': { opacity: 0.82, transform: 'scale(1.06)', transition: '0.15s' },
          }}
        >
          <Box component="img" src={src} alt={label}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </Box>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg"
        PaperProps={{ sx: { background: 'rgba(10,18,28,0.97)', border: 'none', borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ position: 'relative' }}>
          <IconButton onClick={() => setOpen(false)}
            sx={{ position: 'absolute', top: 10, right: 50, zIndex: 2, color: '#fff', background: 'rgba(0,0,0,0.4)' }}>
            <Close />
          </IconButton>
          <Box component="a" href={src} target="_blank" rel="noreferrer"
            sx={{ position: 'absolute', top: 10, right: 10, zIndex: 2 }}>
            <IconButton sx={{ color: '#fff', background: 'rgba(0,0,0,0.4)' }}><OpenInNew fontSize="small" /></IconButton>
          </Box>
          <Box component="img" src={src} alt={label}
            sx={{ display: 'block', maxHeight: '80vh', maxWidth: '85vw', objectFit: 'contain' }} />
          <Box sx={{ px: 2, py: 1 }}>
            <Typography sx={{ color: '#aaa', fontSize: '0.82rem' }}>{prettyKey(label)}</Typography>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}

// ── Entry detail modal ────────────────────────────────────────────────────────
function EntryModal({ entry, open, onClose }) {
  if (!entry) return null;
  const flat   = flatFormData(entry.formData);
  const imgs   = extractImages(entry.formData);
  const loc    = getLocation(entry);
  const author = entry.author;

  // Separate text fields from images/location
  const textFields = Object.entries(flat).filter(([k, v]) => {
    if (k === 'location') return false;
    if (Array.isArray(v) && v[0]?.url) return false;
    if (v && typeof v === 'object' && v.url) return false;
    return true;
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: '98vw', md: '780px' },
          maxHeight: '90vh', borderRadius: '20px',
          background: '#fff',
          boxShadow: '0 24px 64px rgba(15,33,55,0.18)',
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '90vh' }}>

        {/* Header */}
        <Box sx={{
          p: 3, flexShrink: 0,
          background: 'linear-gradient(135deg, rgba(16,117,186,0.08), rgba(0,168,130,0.04))',
          borderBottom: '1px solid rgba(16,117,186,0.1)',
          position: 'relative',
        }}>
          <IconButton onClick={onClose} size="small"
            sx={{ position: 'absolute', top: 12, right: 12, color: '#4a6a85' }}>
            <Close fontSize="small" />
          </IconButton>

          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box sx={{
              width: 48, height: 48, borderRadius: '12px', flexShrink: 0,
              background: 'linear-gradient(135deg, #1075BA, #1a8fd6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(16,117,186,0.3)',
            }}>
              <Assignment sx={{ color: '#fff', fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f2137', lineHeight: 1.2, mb: 0.5 }}>
                {entry.taskLabel || entry.taskId}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={entry.synced ? 'Synced' : 'Offline'} size="small"
                  sx={{
                    background: entry.synced ? '#e6f9f4' : '#fff4e0',
                    color: entry.synced ? '#0a9e74' : '#c06000',
                    border: `1px solid ${entry.synced ? '#b3ead9' : '#ffd599'}`,
                    fontWeight: 700, fontSize: '0.7rem', height: 22,
                  }} />
                {author && (
                  <Chip
                    avatar={<Avatar sx={{ width: 18, height: 18, fontSize: '0.6rem' }}>{author.firstName?.[0]}</Avatar>}
                    label={`${author.firstName} ${author.lastName}`}
                    size="small"
                    sx={{ background: '#f0f4f8', color: '#0f2137', fontSize: '0.7rem', height: 22 }}
                  />
                )}
                {entry.createdAt && (
                  <Chip
                    icon={<AccessTime sx={{ fontSize: '12px !important' }} />}
                    label={format(new Date(entry.createdAt), 'MMM d, yyyy hh:mm a')}
                    size="small"
                    sx={{ background: '#f0f4f8', color: '#4a6a85', fontSize: '0.7rem', height: 22 }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <Stack spacing={3}>

            {/* Form fields */}
            {textFields.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#1075BA', mb: 1.5 }}>
                  Form Data
                </Typography>
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: 1.5,
                }}>
                  {textFields.map(([k, v]) => (
                    <Box key={k} sx={{
                      background: '#f5f8fc', borderRadius: '10px',
                      px: 1.75, py: 1.25,
                      border: '1px solid rgba(16,117,186,0.1)',
                    }}>
                      <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, color: '#4a6a85', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>
                        {prettyKey(k)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.88rem', color: '#0f2137', fontWeight: 500 }}>
                        {String(v)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Photos */}
            {imgs.length > 0 && (
              <>
                <Divider sx={{ borderColor: 'rgba(16,117,186,0.1)' }} />
                <Box>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#1075BA', mb: 1.5 }}>
                    Photos
                  </Typography>
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: 1.5,
                  }}>
                    {imgs.map((img, i) => (
                      <Box
                        key={i}
                        component="a" href={img.url} target="_blank" rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          borderRadius: '12px', overflow: 'hidden', position: 'relative',
                          border: '1px solid rgba(16,117,186,0.15)',
                          boxShadow: '0 2px 8px rgba(15,33,55,0.08)',
                          cursor: 'zoom-in',
                          '&:hover .ph-over': { opacity: 1 },
                          '&:hover img': { transform: 'scale(1.04)' },
                        }}
                      >
                        <Box component="img" src={img.url} alt={img.label}
                          sx={{ width: '100%', height: 130, objectFit: 'cover', display: 'block', transition: 'transform 0.3s' }} />
                        <Box className="ph-over" sx={{
                          position: 'absolute', inset: 0,
                          background: 'rgba(16,117,186,0.3)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: 0, transition: 'opacity 0.2s',
                        }}>
                          <OpenInNew sx={{ color: '#fff', fontSize: 22 }} />
                        </Box>
                        <Box sx={{
                          position: 'absolute', bottom: 0, left: 0, right: 0,
                          background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
                          px: 1, py: 0.75,
                        }}>
                          <Typography sx={{ fontSize: '0.68rem', color: '#fff', fontWeight: 600 }}>
                            {prettyKey(img.label)}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </>
            )}

            {/* Location */}
            {loc && (
              <>
                <Divider sx={{ borderColor: 'rgba(16,117,186,0.1)' }} />
                <Box>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#1075BA', mb: 1.5 }}>
                    Location
                  </Typography>
                  <Stack direction="row" spacing={1.5} flexWrap="wrap">
                    {[
                      { l: 'Latitude',  v: loc.lat },
                      { l: 'Longitude', v: loc.lng },
                      ...(entry.formData?.location?.accuracy != null
                        ? [{ l: 'Accuracy', v: `${parseFloat(entry.formData.location.accuracy).toFixed(1)} m` }]
                        : []),
                    ].map(({ l, v }) => (
                      <Box key={l} sx={{ background: '#f5f8fc', borderRadius: '10px', px: 2, py: 1.25, border: '1px solid rgba(16,117,186,0.1)' }}>
                        <Typography sx={{ fontSize: '0.68rem', color: '#4a6a85', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>{l}</Typography>
                        <Typography sx={{ fontSize: '0.83rem', color: '#0f2137', fontFamily: 'monospace' }}>{v}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </>
            )}

          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function TasksDashboardPage() {
  const token = useSelector(selectToken);

  const mapRef      = useRef(null);
  const mapInstance = useRef(null);
  const markersRef  = useRef({});
  const leafletRef  = useRef(null);

  const today     = format(new Date(), 'yyyy-MM-dd');
  const yearStart = `${new Date().getFullYear()}-01-01`;

  const [entries,      setEntries]      = useState([]);
  const [filtered,     setFiltered]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [search,       setSearch]       = useState('');
  const [dateFrom,     setDateFrom]     = useState(yearStart);
  const [dateTo,       setDateTo]       = useState(today);
  const [taskFilter,   setTaskFilter]   = useState('ALL');
  const [taskOptions,  setTaskOptions]  = useState([]);
  const [selectedEntry,setSelectedEntry]= useState(null);
  const [modalOpen,    setModalOpen]    = useState(false);
  const [view,         setView]         = useState('map');
  const [leafletReady, setLeafletReady] = useState(false);
  const [exporting,    setExporting]    = useState(false);

  // invalidate map size when toggling back
  useEffect(() => {
    if (view === 'map' && mapInstance.current) {
      setTimeout(() => mapInstance.current.invalidateSize(), 50);
    }
  }, [view]);

  // ── Leaflet CDN ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (window.L) { leafletRef.current = window.L; setLeafletReady(true); return; }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
    document.head.appendChild(link);
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => { leafletRef.current = window.L; setLeafletReady(true); };
    document.head.appendChild(script);
  }, []);

  // ── Fetch entries ────────────────────────────────────────────────────────────
  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      // Convert yyyy-MM-dd → MM-DD-YYYY for the API
      const fmtApi = (d) => {
        const [y, m, dd] = d.split('-');
        return `${m}-${dd}-${y}`;
      };
      const url = `${API_BASE}/api/v1/admin/tasks?dateFrom=${fmtApi(dateFrom)}&dateTo=${fmtApi(dateTo)}&limit=500&page=1`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      const rows = data.entries || [];
      setEntries(rows);
      // Build unique task type options
      const types = [...new Set(rows.map((r) => r.taskLabel || r.taskId).filter(Boolean))].sort();
      setTaskOptions(types);
    } catch (e) { console.error('Tasks fetch failed', e); }
    setLoading(false);
  }, [dateFrom, dateTo, token]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // ── Filter ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    let out = entries;
    if (search) {
      const q = search.toLowerCase();
      out = out.filter((e) => {
        const author = e.author;
        const name = `${author?.firstName || ''} ${author?.lastName || ''}`.toLowerCase();
        return name.includes(q) || (e.taskLabel || '').toLowerCase().includes(q) || (e.taskId || '').toLowerCase().includes(q);
      });
    }
    if (taskFilter !== 'ALL') {
      out = out.filter((e) => (e.taskLabel || e.taskId) === taskFilter);
    }
    setFiltered(out);
  }, [entries, search, taskFilter]);

  // ── Init map ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!leafletReady || !mapRef.current || mapInstance.current) return;
    const L = leafletRef.current;

    mapInstance.current = L.map(mapRef.current, {
      center: [14.5995, 120.9842], zoom: 11, zoomControl: false,
    });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OpenStreetMap &copy; CARTO', maxZoom: 19,
    }).addTo(mapInstance.current);

    L.control.zoom({ position: 'bottomright' }).addTo(mapInstance.current);

    const style = document.createElement('style');
    style.textContent = `
      .td-popup-wrapper {
        background: #ffffff !important;
        border: 1px solid rgba(16,117,186,0.2) !important;
        border-radius: 14px !important;
        box-shadow: 0 8px 32px rgba(15,33,55,0.15) !important;
        padding: 0 !important; overflow: hidden !important;
      }
      .td-popup-wrapper .leaflet-popup-tip { background: #ffffff !important; }
      .td-popup-wrapper .leaflet-popup-content { margin: 0 !important; }
      .td-marker { cursor: pointer !important; }
      .td-marker div { transition: transform 0.2s; }
      .td-marker:hover div { transform: scale(1.15) !important; }
    `;
    document.head.appendChild(style);
  }, [leafletReady]);

  // ── Update markers ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapInstance.current || !leafletReady) return;
    const L   = leafletRef.current;
    const map = mapInstance.current;

    Object.values(markersRef.current).forEach((m) => map.removeLayer(m));
    markersRef.current = {};

    const withLoc = filtered.filter((e) => getLocation(e));
    if (!withLoc.length) return;
    const bounds = [];

    // Colour palette cycling through task types
    const palette = ['#1075BA', '#0a9e74', '#7b1fa2', '#c06000', '#d32f2f', '#0288d1'];
    const typeColors = {};
    [...new Set(withLoc.map((e) => e.taskLabel || e.taskId))].forEach((t, i) => {
      typeColors[t] = palette[i % palette.length];
    });

    withLoc.forEach((entry) => {
      const loc    = getLocation(entry);
      const color  = typeColors[entry.taskLabel || entry.taskId] || '#1075BA';
      const author = entry.author;
      const name   = author ? `${author.firstName || ''} ${author.lastName || ''}`.trim() : '—';
      const label  = entry.taskLabel || entry.taskId || 'Task';
      const time   = entry.createdAt ? format(new Date(entry.createdAt), 'MMM d hh:mm a') : '';
      const imgs   = extractImages(entry.formData);
      const thumbUrl = imgs[0]?.url;

      const iconHtml = `
        <div style="position:relative;width:44px;height:44px;">
          <div style="
            width:44px;height:44px;border-radius:50%;
            border:3px solid ${color};
            box-shadow:0 4px 14px rgba(15,33,55,0.18),0 0 0 3px ${color}33;
            overflow:hidden;background:#f0f4f8;
            display:flex;align-items:center;justify-content:center;
          ">
            ${thumbUrl
              ? `<img src="${thumbUrl}" style="width:100%;height:100%;object-fit:cover;" onerror="this.style.display='none'"/>`
              : `<svg width="20" height="20" viewBox="0 0 24 24" fill="${color}"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/></svg>`
            }
          </div>
          <div style="
            position:absolute;bottom:0;right:0;
            width:13px;height:13px;border-radius:50%;
            background:${color};border:2px solid #fff;
            box-shadow:0 1px 4px rgba(0,0,0,0.2);
          "></div>
        </div>
      `;

      const icon = L.divIcon({
        html: iconHtml, className: 'td-marker',
        iconSize: [44, 44], iconAnchor: [22, 22], popupAnchor: [0, -28],
      });

      const marker = L.marker([loc.lat, loc.lng], { icon }).addTo(map);

      const popupHtml = `
        <div style="padding:12px 14px;min-width:190px;">
          <div style="display:flex;align-items:center;gap:9px;margin-bottom:8px;">
            <div style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></div>
            <div style="font-weight:700;font-size:13px;color:#0f2137;flex:1;">${label}</div>
          </div>
          <div style="font-size:11px;color:#4a6a85;margin-bottom:6px;">
            <span>👤 ${name}</span>
          </div>
          ${time ? `<div style="font-size:11px;color:#6b7f8e;margin-bottom:8px;">🕐 ${time}</div>` : ''}
          ${thumbUrl ? `<img src="${thumbUrl}" style="width:100%;height:70px;object-fit:cover;border-radius:8px;margin-bottom:6px;border:1px solid rgba(16,117,186,0.15);" />` : ''}
          <div style="font-size:11px;color:#9aacb8;text-align:center;padding-top:4px;border-top:1px solid #eef2f7;">Click for full details</div>
        </div>
      `;

      marker.bindPopup(popupHtml, {
        maxWidth: 220, closeButton: false,
        className: 'td-popup-wrapper',
      });
      marker.on('mouseover', () => marker.openPopup());
      marker.on('mouseout',  () => marker.closePopup());
      marker.on('click', () => { setSelectedEntry(entry); setModalOpen(true); });

      markersRef.current[entry._id] = marker;
      bounds.push([loc.lat, loc.lng]);
    });

    if (bounds.length) {
      try { map.fitBounds(bounds, { padding: [60, 60], maxZoom: 14 }); } catch {}
    }
  }, [filtered, leafletReady]);

  // ── Stats ────────────────────────────────────────────────────────────────────
  const totalWithLoc = filtered.filter((e) => getLocation(e)).length;
  const totalSynced  = entries.filter((e) => e.synced).length;

  const handleExport = async () => {
    setExporting(true);
    try { await exportTasksExcel(filtered.length ? filtered : entries, dateFrom, dateTo); }
    catch (e) { console.error('Export failed', e); }
    setExporting(false);
  };

  return (
        <Box sx={{ height: 'calc(94vh)', display: 'flex', flexDirection: 'column', gap: 2 }}>


      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f2137', mb: 0.25 }}>
            Task Entries
          </Typography>
          <Typography variant="body2" sx={{ color: '#4a6a85' }}>
            Field task submissions with location and photo data
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {[
            { label: 'Total Entries', count: entries.length,  color: '#1075BA', bg: '#e6f2ff', border: '#99ccf5' },
            { label: 'On Map',        count: totalWithLoc,    color: '#0a9e74', bg: '#e6f9f4', border: '#b3ead9' },
            { label: 'Synced',        count: totalSynced,     color: '#7b1fa2', bg: '#f5e6ff', border: '#d9b3f5' },
          ].map((s) => (
            <Box key={s.label} sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              px: 1.5, py: 0.75, borderRadius: '10px',
              background: s.bg, border: `1px solid ${s.border}`,
            }}>
              <FiberManualRecord sx={{ fontSize: 8, color: s.color }} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: s.color }}>
                {s.count} {s.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Controls */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" flexWrap="wrap">
        <TextField
          size="small" placeholder="Search by author or task…"
          value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 180 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: '#4a6a85', fontSize: 18 }} /></InputAdornment> }}
        />
        {/* Date range */}
        <TextField size="small" type="date" label="From" value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          sx={{ width: 150 }} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="To" value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          sx={{ width: 150 }} InputLabelProps={{ shrink: true }} />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Task Type</InputLabel>
          <Select value={taskFilter} onChange={(e) => setTaskFilter(e.target.value)} label="Task Type">
            <MenuItem value="ALL">All Types</MenuItem>
            {taskOptions.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>
        </FormControl>

        <ToggleButtonGroup value={view} exclusive onChange={(_, v) => v && setView(v)} size="small">
          <ToggleButton value="map"  sx={{ px: 1.5 }}><Map sx={{ fontSize: 18 }} /></ToggleButton>
          <ToggleButton value="list" sx={{ px: 1.5 }}><ViewList sx={{ fontSize: 18 }} /></ToggleButton>
        </ToggleButtonGroup>

        <Button variant="contained" size="small"
          startIcon={exporting ? <CircularProgress size={14} color="inherit" /> : <FileDownload />}
          onClick={handleExport} disabled={exporting || entries.length === 0}
          sx={{ whiteSpace: 'nowrap', px: 2 }}>
          {exporting ? 'Exporting…' : 'Export Excel'}
        </Button>

        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchEntries} disabled={loading}
            sx={{ color: '#1075BA', background: alpha('#1075BA', 0.08), border: `1px solid ${alpha('#1075BA', 0.2)}`, borderRadius: '10px', width: 36, height: 36 }}>
            {loading ? <CircularProgress size={16} sx={{ color: '#1075BA' }} /> : <Refresh fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      {/* ── MAP VIEW — always mounted ─────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: view === 'map' ? 'flex' : 'none', gap: 2, minHeight: 0 }}>

        {/* Map */}
        <Box sx={{
          flex: 1, borderRadius: '16px', overflow: 'hidden',
          border: '1px solid rgba(16,117,186,0.15)',
          boxShadow: '0 2px 12px rgba(15,33,55,0.07)',
          position: 'relative', minHeight: 0,
        }}>
          <Box ref={mapRef} sx={{ width: '100%', height: '100%' }} />
          {!leafletReady && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f4f8' }}>
              <CircularProgress sx={{ color: '#1075BA' }} />
            </Box>
          )}
          <Box sx={{
            position: 'absolute', top: 12, left: 12, zIndex: 1000,
            background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(16,117,186,0.2)', borderRadius: '10px',
            px: 1.5, py: 0.75, boxShadow: '0 2px 8px rgba(15,33,55,0.1)',
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            <MyLocation sx={{ fontSize: 14, color: '#1075BA' }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f2137' }}>
              {totalWithLoc} on map
            </Typography>
          </Box>
        </Box>

        {/* Sidebar list */}
        <Card sx={{ width: 290, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ px: 1.5, py: 1.25, borderBottom: '1px solid rgba(16,117,186,0.08)' }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#4a6a85', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {filtered.length} Entries
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
                <CircularProgress sx={{ color: '#1075BA' }} size={28} />
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Assignment sx={{ fontSize: 40, color: '#c8d4dc', mb: 1 }} />
                <Typography sx={{ color: '#6b7f8e', fontSize: '0.875rem' }}>No entries found</Typography>
              </Box>
            ) : filtered.map((entry) => {
              const imgs   = extractImages(entry.formData);
              const author = entry.author;
              const isSelected = selectedEntry?._id === entry._id;

              return (
                <Box key={entry._id} onClick={() => {
                  setSelectedEntry(entry); setModalOpen(true);
                  const loc = getLocation(entry);
                  if (loc && mapInstance.current) {
                    mapInstance.current.flyTo([loc.lat, loc.lng], 16, { duration: 1.2 });
                    markersRef.current[entry._id]?.openPopup();
                  }
                }}
                  sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1.25,
                    p: 1.25, borderRadius: '12px', cursor: 'pointer',
                    background: isSelected ? alpha('#1075BA', 0.07) : 'transparent',
                    border: `1px solid ${isSelected ? alpha('#1075BA', 0.3) : 'transparent'}`,
                    mb: 0.5,
                    '&:hover': { background: alpha('#1075BA', 0.05), border: `1px solid ${alpha('#1075BA', 0.15)}` },
                  }}
                >
                  {/* Thumb */}
                  <Box sx={{
                    width: 42, height: 42, borderRadius: '10px', overflow: 'hidden',
                    flexShrink: 0, background: '#f0f4f8',
                    border: '1.5px solid rgba(16,117,186,0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {imgs[0]
                      ? <Box component="img" src={imgs[0].url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <Assignment sx={{ fontSize: 20, color: '#9aacb8' }} />
                    }
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#0f2137', lineHeight: 1.2, mb: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.taskLabel || entry.taskId}
                    </Typography>
                    {author && (
                      <Typography sx={{ fontSize: '0.7rem', color: '#4a6a85', mb: 0.2 }}>
                        {author.firstName} {author.lastName}
                      </Typography>
                    )}
                    {entry.createdAt && (
                      <Typography sx={{ fontSize: '0.68rem', color: '#9aacb8' }}>
                        {format(new Date(entry.createdAt), 'MMM d, hh:mm a')}
                      </Typography>
                    )}
                  </Box>
                  {imgs.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      <CameraAlt sx={{ fontSize: 13, color: '#9aacb8' }} />
                      <Typography sx={{ fontSize: '0.68rem', color: '#9aacb8' }}>{imgs.length}</Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Card>
      </Box>

      {/* ── LIST VIEW ── */}
      {view === 'list' && (
        <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['#', 'Task', 'Author', 'Synced', 'Date', 'Form Data', 'Photos', 'Location'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', whiteSpace: 'nowrap', py: 1.25 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 8 }).map((_, j) => (
                        <TableCell key={j}><Box sx={{ height: 16, borderRadius: 1, background: 'rgba(16,117,186,0.07)' }} /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, color: '#6b7f8e' }}>
                      No entries found for the selected date range
                    </TableCell>
                  </TableRow>
                ) : filtered.map((entry, idx) => {
                  const flat     = flatFormData(entry.formData);
                  const imgs     = extractImages(entry.formData);
                  const loc      = getLocation(entry);
                  const author   = entry.author;
                  const isSelected = selectedEntry?._id === entry._id;

                  // Key text fields (skip images/location)
                  const textFields = Object.entries(flat).filter(([k, v]) => {
                    if (k === 'location') return false;
                    if (Array.isArray(v) && v[0]?.url) return false;
                    if (v && typeof v === 'object' && v.url) return false;
                    return true;
                  });

                  return (
                    <TableRow key={entry._id} hover
                      onClick={() => { setSelectedEntry(entry); setModalOpen(true); }}
                      sx={{
                        cursor: 'pointer',
                        background: isSelected ? alpha('#1075BA', 0.06) : 'inherit',
                        '&:hover': { background: alpha('#1075BA', 0.04) },
                      }}
                    >
                      {/* # */}
                      <TableCell sx={{ color: '#9aacb8', fontSize: '0.78rem', width: 36 }}>{idx + 1}</TableCell>

                      {/* Task */}
                      <TableCell sx={{ minWidth: 150 }}>
                        <Typography sx={{ fontSize: '0.83rem', fontWeight: 600, color: '#0f2137' }}>
                          {entry.taskLabel || entry.taskId}
                        </Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: '#9aacb8', fontFamily: 'monospace' }}>
                          {entry._id}
                        </Typography>
                      </TableCell>

                      {/* Author */}
                      <TableCell sx={{ minWidth: 140 }}>
                        {author ? (
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', background: alpha('#1075BA', 0.15), color: '#1075BA' }}>
                              {author.firstName?.[0]}
                            </Avatar>
                            <Typography sx={{ fontSize: '0.8rem', color: '#0f2137' }}>
                              {author.firstName} {author.lastName}
                            </Typography>
                          </Stack>
                        ) : <Typography sx={{ color: '#9aacb8', fontSize: '0.8rem' }}>—</Typography>}
                      </TableCell>

                      {/* Synced */}
                      <TableCell>
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', gap: 0.5,
                          fontSize: '0.68rem', fontWeight: 700,
                          color: entry.synced ? '#0a9e74' : '#c06000',
                          background: entry.synced ? '#e6f9f4' : '#fff4e0',
                          border: `1px solid ${entry.synced ? '#b3ead9' : '#ffd599'}`,
                          px: 0.75, py: 0.3, borderRadius: '6px',
                        }}>
                          {entry.synced
                            ? <CheckCircle sx={{ fontSize: 11 }} />
                            : <Cancel sx={{ fontSize: 11 }} />}
                          {entry.synced ? 'Synced' : 'Offline'}
                        </Box>
                      </TableCell>

                      {/* Date */}
                      <TableCell sx={{ minWidth: 130 }}>
                        {entry.createdAt && (
                          <Box>
                            <Typography sx={{ fontSize: '0.8rem', color: '#0f2137' }}>
                              {format(new Date(entry.createdAt), 'MMM d, yyyy')}
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: '#9aacb8', fontFamily: 'monospace' }}>
                              {format(new Date(entry.createdAt), 'hh:mm a')}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>

                      {/* Form data — show key chips */}
                      <TableCell sx={{ minWidth: 200 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {textFields.slice(0, 4).map(([k, v]) => (
                            <Box key={k} sx={{
                              fontSize: '0.68rem', background: '#f5f8fc',
                              border: '1px solid rgba(16,117,186,0.12)',
                              borderRadius: '6px', px: 0.75, py: 0.2,
                              color: '#4a6a85', whiteSpace: 'nowrap',
                            }}>
                              <span style={{ fontWeight: 700, color: '#1075BA' }}>{prettyKey(k)}:</span> {String(v)}
                            </Box>
                          ))}
                          {textFields.length > 4 && (
                            <Box sx={{ fontSize: '0.68rem', color: '#9aacb8', px: 0.5, display: 'flex', alignItems: 'center' }}>
                              +{textFields.length - 4} more
                            </Box>
                          )}
                        </Box>
                      </TableCell>

                      {/* Photos */}
                      <TableCell sx={{ minWidth: 110 }}>
                        <Stack direction="row" spacing={0.5}>
                          {imgs.slice(0, 3).map((img, i) => (
                            <PhotoThumb key={i} src={img.url} label={img.label} />
                          ))}
                          {imgs.length > 3 && (
                            <Box sx={{
                              width: 40, height: 40, borderRadius: '8px',
                              background: '#f0f4f8', border: '1.5px solid rgba(16,117,186,0.15)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Typography sx={{ fontSize: '0.7rem', color: '#4a6a85', fontWeight: 700 }}>
                                +{imgs.length - 3}
                              </Typography>
                            </Box>
                          )}
                          {imgs.length === 0 && (
                            <Typography sx={{ fontSize: '0.75rem', color: '#c8d4dc', fontStyle: 'italic' }}>—</Typography>
                          )}
                        </Stack>
                      </TableCell>

                      {/* Location */}
                      <TableCell sx={{ minWidth: 120 }}>
                        {loc ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationOn sx={{ fontSize: 13, color: '#1075BA' }} />
                            <Box>
                              <Typography sx={{ fontSize: '0.72rem', color: '#0f2137', fontFamily: 'monospace' }}>
                                {loc.lat.toFixed(4)}
                              </Typography>
                              <Typography sx={{ fontSize: '0.72rem', color: '#9aacb8', fontFamily: 'monospace' }}>
                                {loc.lng.toFixed(4)}
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: '0.75rem', color: '#c8d4dc', fontStyle: 'italic' }}>—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid rgba(16,117,186,0.08)', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.75rem', color: '#4a6a85' }}>
              Showing <strong>{filtered.length}</strong> of <strong>{entries.length}</strong> entries · Click any row for full details
            </Typography>
          </Box>
        </Card>
      )}

      {/* Detail modal */}
      <EntryModal entry={selectedEntry} open={modalOpen} onClose={() => setModalOpen(false)} />
    </Box>
  );
}
